import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { PREDEFINED_TASKS } from "@/components/ai/ai-constants";
import { ChatInput, type ChatInputActiveState, type NewPendingMessage } from "@/components/ai/chat/ChatInput";
import { ChatInputShell } from "@/components/ai/chat/ChatInputShell";
import ChatInputWithPrompts from "@/components/ai/chat/ChatInputWithPrompts";
import { ChatMessages, type ChatMessagesHandle } from "@/components/ai/chat/ChatMessages";
import { ScrollToBottomButton } from "@/components/ai/chat/ScrollToBottomButton";
import {
    agentExecutionInputAtomFamily,
    agentModeAtomFamily,
    selectedDevRunnerIdAtomFamily,
} from "@/components/ai/chat/state";
import { StopButton } from "@/components/ai/chat/StopButton";
import { DevRunnerSelector } from "@/components/ai/DevRunnerSelector";
import {
    AgentMode,
    applyAgentModePrefix,
    getAgentModeFloodBgVar,
    getAgentModePlaceholder,
} from "@/components/ai/prompt-input/agent-mode";
import { AgentModeDropdown } from "@/components/ai/prompt-input/AgentModeDropdown";
import { AddImageButton, type ImageAttachment } from "@/components/ai/prompt-input/ImageUpload";
import { ImageAttachmentStrip } from "@/components/ai/prompt-input/ImageAttachmentStrip";
import { useImageDrop, useImagePaste } from "@/components/ai/prompt-input/use-image-drop";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { PromptTemplate } from "@/components/flexkit/PromptTemplate";
import { ScrollArea, type ScrollAreaHandle } from "@/components/flexkit/ScrollArea";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { Switch } from "@/components/Switch";
import { Tooltip } from "@/components/Tooltip";
import { useEnvironmentClassMap } from "@/hooks/use-environment-class-entries";
import { useExperimentEnabled } from "@/hooks/use-experiment";
import { useFlagAgentImageAttachmentsEnabled, useFlagRunnerSideAgentEnabled } from "@/hooks/use-flag";
import { useVscodePerfInstrumentationEnabled } from "@/hooks/use-perf-instrumentation";

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { type AgentMessageFragment, type Message, type UserMessage } from "@/hooks/use-stream-conversation";
import { useTheme } from "@/hooks/use-theme";
import { useVSCodeIpcContext } from "@/hooks/use-vscode-ipc";
import type { PendingInteractiveFragment } from "@/components/ai/pending-interactive-utils";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import {
    ANNOTATION_RSA_CAPABLE,
    resolveAgentId,
    useCreateAgentExecution,
    useSendToAgentExecution,
} from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { getVSCodeURL } from "@/utils/custom-domain";
import { formatError } from "@/utils/errors";
import { isTouchDevice } from "@/utils/is-touch-device";
import { OpenInEditorKeyBinding } from "@/utils/platform";
import { VSCodeIPC } from "@/vscode-ipc";
import {
    AgentCodeContext,
    AgentExecution_Phase,
    SendToAgentExecutionRequest,
    SupportedModel,
    UserInputBlock,
    UserInputBlock_ImageInput,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { getSingleModelDisplayName } from "@/components/runners/details/llm-integration-utils";
import { useAtom } from "jotai";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { v4 as uuidv4 } from "uuid";

const OnaWaiting = lazy(() => import("@/components/ona/OnaWaiting").then((m) => ({ default: m.OnaWaiting })));

// TODO: consolidate toolbar with ChatInput to reduce duplication
const ChatInputStyled: FC<{ environmentId: string }> = ({ environmentId }) => {
    const createAgentExecution = useCreateAgentExecution();
    const sendToAgentExecution = useSendToAgentExecution();
    const { toast } = useToast();
    const isRalphModeEnabled = useExperimentEnabled("ralph");
    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();
    const { value: rsaEnabled } = useFlagRunnerSideAgentEnabled();
    const { data: environment } = useEnvironment(environmentId);
    const { environmentClasses } = useEnvironmentClassMap();
    const envClassEntry = environment?.spec?.machine?.class
        ? environmentClasses[environment.spec.machine.class]
        : undefined;

    const [inputValue, setInputValue] = useAtom(agentExecutionInputAtomFamily(environmentId));
    const [agentMode, setAgentMode] = useAtom(agentModeAtomFamily(environmentId));
    const [selectedDevRunnerId] = useAtom(selectedDevRunnerIdAtomFamily(environmentId));
    const [selectedImages, setSelectedImages] = useState<ImageAttachment[]>([]);

    const handleImagesSelected = useCallback((images: ImageAttachment[]) => {
        setSelectedImages((prev) => [...prev, ...images]);
    }, []);

    const handleRemoveImage = useCallback((id: string) => {
        setSelectedImages((prev) => {
            const image = prev.find((img) => img.id === id);
            if (image) {
                URL.revokeObjectURL(image.preview);
            }
            return prev.filter((img) => img.id !== id);
        });
    }, []);

    const { isDragging, dragHandlers } = useImageDrop(handleImagesSelected, !imageAttachmentsEnabled);
    const { handlePaste } = useImagePaste(handleImagesSelected, !imageAttachmentsEnabled);

    const availableAgentModes = useMemo(() => {
        const modes: AgentMode[] = [AgentMode.Agent, AgentMode.Spec];
        if (isRalphModeEnabled) {
            modes.push(AgentMode.Ralph);
        }
        return modes;
    }, [isRalphModeEnabled]);

    const showAgentModeDropdown = availableAgentModes.length > 1;

    const handleSendMessage = useCallback(
        async (text: string) => {
            if (!text.trim()) return;

            const messageToSend = applyAgentModePrefix(text, agentMode);
            const imagesToSend = imageAttachmentsEnabled ? [...selectedImages] : undefined;

            try {
                const { agentId, runnerId } = resolveAgentId({
                    devRunnerId: selectedDevRunnerId ?? undefined,
                    rsaEnabled,
                    rsaCapable: environment?.metadata?.annotations?.[ANNOTATION_RSA_CAPABLE] === "true",
                    runnerId: envClassEntry?.runner?.runnerId,
                });
                const { agentExecutionId } = await createAgentExecution.mutateAsync({
                    agentId,
                    codeContext: new AgentCodeContext({
                        context: {
                            case: "environmentId",
                            value: environmentId,
                        },
                    }),
                    runnerId,
                });

                const inputs: UserInputBlock_Input[] = [
                    new UserInputBlock_Input({
                        input: {
                            case: "text",
                            value: new UserInputBlock_TextInput({
                                content: messageToSend,
                            }),
                        },
                    }),
                ];

                for (const image of imagesToSend ?? []) {
                    inputs.push(
                        new UserInputBlock_Input({
                            input: {
                                case: "image",
                                value: new UserInputBlock_ImageInput({
                                    data: image.data,
                                    mimeType: image.mimeType,
                                }),
                            },
                        }),
                    );
                }

                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecutionId,
                        input: {
                            case: "userInput" as const,
                            value: new UserInputBlock({
                                id: uuidv4(),
                                inputs,
                            }),
                        },
                    }),
                );

                setInputValue("");
                for (const image of imagesToSend ?? []) {
                    URL.revokeObjectURL(image.preview);
                }
                setSelectedImages([]);
            } catch (error) {
                toast({
                    title: "Error starting conversation",
                    description: formatError(error),
                });
            }
        },
        [
            createAgentExecution,
            environmentId,
            sendToAgentExecution,
            setInputValue,
            toast,
            agentMode,
            imageAttachmentsEnabled,
            selectedImages,
            selectedDevRunnerId,
            rsaEnabled,
            envClassEntry,
            environment?.metadata?.annotations,
        ],
    );

    const onKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // On touch devices, let Enter insert newlines (users submit via Send button)
            // On desktop, Enter submits and Shift+Enter inserts newlines
            if (e.key === "Enter" && !e.shiftKey && !isTouchDevice()) {
                const input = e.target as HTMLTextAreaElement;
                e.preventDefault();
                e.stopPropagation();
                await handleSendMessage(input.value);
                return;
            }
        },
        [handleSendMessage],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    const isInputEmpty = !inputValue.trim() && selectedImages.length === 0;

    return (
        <ChatInputShell isDragging={isDragging} dragHandlers={dragHandlers} agentMode={agentMode}>
            <ChatInputWithPrompts
                minHeight={40}
                maxHeight={300}
                popoverSide="top"
                popoverSideOffset={20} // top padding + border + some spacing
                popoverWidthOffset={32} // account for the total horizontal padding (px-4)
                popoverAlignOffset={-16} // account for the left padding
                placeholder={getAgentModePlaceholder(agentMode)}
                onKeyDown={onKeyDown}
                onChange={handleChange}
                onPaste={handlePaste}
                value={inputValue}
                className="placeholder:text-content-inactive"
                data-testid="chat-input-styled"
                autoFocus
                enterKeyHint="enter"
            />
            {imageAttachmentsEnabled && selectedImages.length > 0 && (
                <ImageAttachmentStrip images={selectedImages} onRemove={handleRemoveImage} />
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Left side: Add image button and tools button */}
                <div className="-ml-[7.5px] flex min-w-0 flex-1 flex-wrap items-center">
                    {imageAttachmentsEnabled && <AddImageButton onImagesSelected={handleImagesSelected} />}
                </div>
                {/* Right side: Dev runner selector, agent mode dropdown and send button */}
                <div className="-mr-2 flex min-w-0 items-center gap-2">
                    <DevRunnerSelector environmentId={environmentId} />
                    {showAgentModeDropdown && (
                        <AgentModeDropdown
                            mode={agentMode}
                            onModeChange={setAgentMode}
                            availableModes={availableAgentModes}
                        />
                    )}
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                Send
                                <div className="flex items-center gap-1">
                                    <KeyboardKey>Enter</KeyboardKey>
                                </div>
                            </div>
                        }
                    >
                        <Button
                            id="send-button"
                            className="-ml-1 size-9 p-0"
                            LeadingIcon={IconArrowUp}
                            aria-label="Send message"
                            onClick={() => handleSendMessage(inputValue)}
                            disabled={isInputEmpty || sendToAgentExecution.isPending}
                            data-tracking-id="send-to-agent-execution-chat-input"
                        />
                    </Tooltip>
                </div>
            </div>
        </ChatInputShell>
    );
};

export type SidebarViewMode = "description" | "vscode";

export type VSCodeBrowserViewProps = {
    editorUrl: string | undefined;
    visible?: boolean;
} & PropsWithClassName;

export const VSCodeBrowserView: FC<VSCodeBrowserViewProps> = ({ editorUrl, className, visible = true }) => {
    const { vscodeIpc, setVscodeIpc } = useVSCodeIpcContext();
    const actualEditorUrl = editorUrl || getVSCodeURL();

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const api = useGitpodAPI();
    const { effectiveTheme } = useTheme();
    const perfEnabled = useVscodePerfInstrumentationEnabled();

    const supportsEnvironmentUrlNavigation = vscodeIpc.current?.supportsEnvironmentUrlNavigation() ?? false;

    useEffect(() => {
        if (!iframeRef.current) {
            return;
        }

        if (supportsEnvironmentUrlNavigation) {
            if (!vscodeIpc.current) {
                setVscodeIpc(new VSCodeIPC(api, iframeRef.current, effectiveTheme, actualEditorUrl, perfEnabled));
            } else {
                vscodeIpc.current.updateEnvironmentUrl(actualEditorUrl);
            }
        } else {
            setVscodeIpc(new VSCodeIPC(api, iframeRef.current, effectiveTheme, actualEditorUrl, perfEnabled));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualEditorUrl]);

    useEffect(() => {
        return () => {
            setVscodeIpc(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connectionId = vscodeIpc.current?.connectionId;
    useEffect(() => {
        void vscodeIpc.current?.setVisibilityState(visible);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, vscodeIpc.current, connectionId]);

    const handleIframeLoad = useCallback(() => {
        if (!iframeRef.current?.contentWindow) {
            return;
        }

        void vscodeIpc.current?.setVisibilityState(visible);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vscodeIpc.current, visible]);

    return (
        <iframe
            ref={iframeRef}
            src={supportsEnvironmentUrlNavigation ? getVSCodeURL() : actualEditorUrl}
            className={cn("h-full w-full border-none", className)}
            sandbox="allow-same-origin allow-pointer-lock allow-scripts allow-downloads allow-forms allow-popups"
            allow="clipboard-read; clipboard-write; keyboard-map; local-network-access;"
            data-testid="vscode-browser-iframe"
            onLoad={handleIframeLoad}
            tabIndex={-1}
        />
    );
};

export const SidebarHeader: FC<{
    content?: AgentMessageFragment;
    viewMode: SidebarViewMode;
    onViewModeChange: (mode: SidebarViewMode) => void;
}> = ({ content, viewMode, onViewModeChange }) => {
    const text = useMemo(() => {
        if (!content) {
            return "No action selected";
        }
        switch (content.type) {
            case "action":
                return "Action";
            case "fileModification":
                return "File modification";
            case "environmentCreation":
                return "Environment creation";
            case "thinking":
                return "Thoughts";
            default:
                return `Unknown: ${content.type}`;
        }
    }, [content]);

    return (
        <div className="flex h-12 w-full items-center justify-between border-b border-border-base px-4 py-3 sm:px-6">
            <Heading1 className="shrink-0 text-lg">{text}</Heading1>
            <Tooltip
                content={
                    <div className="flex flex-row items-center gap-2">
                        <span>Toggle VS Code Browser</span>
                        <KeyboardCombo keys={OpenInEditorKeyBinding} />
                    </div>
                }
            >
                <div className="flex items-center gap-2">
                    <Text className="text-sm">VS Code Browser</Text>
                    <Switch
                        state={viewMode === "vscode" ? "checked" : "unchecked"}
                        onToggle={(checked) => onViewModeChange(checked ? "vscode" : "description")}
                        aria-label="Toggle VS Code Browser"
                    />
                </div>
            </Tooltip>
        </div>
    );
};

const ModelIndicator: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const model = agentExecution.status?.supportedModel;
    if (model === undefined || model === SupportedModel.UNSPECIFIED) {
        return null;
    }
    const displayName = getSingleModelDisplayName(model);
    if (!displayName) {
        return null;
    }
    return (
        <Text className="select-none text-xs text-content-secondary" data-testid="model-indicator">
            {displayName}
        </Text>
    );
};

const LastUserMessageButton: FC<{
    messages: Message[];
}> = ({ messages }) => {
    const lastUserMessage = useMemo(() => {
        if (!messages || messages.length === 0) {
            return null;
        }
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].type === "user") {
                return messages[i] as UserMessage;
            }
        }
        return null;
    }, [messages]);

    const handleScrollToLastUserMessage = useCallback(() => {
        if (!lastUserMessage) {
            return;
        }

        const userMessageElement = document.querySelector(`[data-message-id="user-${lastUserMessage.id}"]`);
        const scrollable = document.querySelector("#conversation-content");

        if (
            userMessageElement &&
            scrollable &&
            userMessageElement instanceof HTMLElement &&
            scrollable instanceof HTMLElement
        ) {
            // scrollIntoView doesn't let us target a specific scroll container
            scrollable.scrollTo({
                top: userMessageElement.offsetTop - scrollable.offsetTop - 10,
                behavior: "smooth",
            });
        }
    }, [lastUserMessage]);

    if (!lastUserMessage) {
        return null;
    }

    return (
        <Tooltip content="Scroll to last user message">
            <Button
                variant="ghost"
                size="xs"
                LeadingIcon={IconArrowUp}
                onClick={handleScrollToLastUserMessage}
                className="ml-2 pl-1 pr-1.5 font-normal"
                data-tracking-id="scroll-to-last-user-message"
            >
                Last user message
            </Button>
        </Tooltip>
    );
};

export const SidebarContent: FC<{
    content?: AgentMessageFragment;
}> = ({ content }) => {
    const children = useMemo(() => {
        if (!content) {
            return <div className="font-mono text-sm">No content</div>;
        }
        switch (content.type) {
            case "action":
                // Intentionally shows raw intentDescription without tense conversion.
                // This is a debug/support-bundle viewer — preserving the exact backend value aids diagnostics.
                return (
                    <div className="flex flex-col gap-2">
                        <div>
                            <Text className="text-sm font-bold">Intent</Text>
                            <div className="font-mono text-sm">{content.intentDescription}</div>
                        </div>
                        <div>
                            <Text className="text-sm font-bold">Result</Text>
                            <div className="font-mono text-sm">{content.resultDescription || "No result"}</div>
                        </div>
                    </div>
                );
            case "fileModification":
                return <div>{content.content}</div>;
            case "environmentCreation":
                return <div>{content.environmentId}</div>;
            case "thinking":
                return (
                    <div className="flex flex-col gap-2">
                        <div className="whitespace-pre-wrap font-mono text-sm">{content.content}</div>
                    </div>
                );
            default:
                return <div>{content.type}</div>;
        }
    }, [content]);

    return (
        <ScrollArea orientation="both">
            <div className="px-4 py-3 sm:px-6">{children}</div>
        </ScrollArea>
    );
};

export const ConversationError: FC<{
    error?: Error;
    loadingMessage?: string;
}> = ({ error, loadingMessage }) => {
    // Conversation URL may be populated before the stream is ready; suppress 404s.
    const is404 = error?.message?.includes("404");

    if (is404) {
        return null;
    }

    if (!error && !loadingMessage) {
        return null;
    }

    const isRetrying = !!loadingMessage;
    const errorText = formatError(error);
    // Strip trailing retry text to avoid duplication with the countdown message (e.g., "Retrying in 3s...")
    const baseErrorText = isRetrying ? errorText.replace(/[.,]?\s*Retrying automatically\.?\s*$/, "") : errorText;
    const bannerText = isRetrying ? `${baseErrorText}, ${loadingMessage.toLowerCase()}` : errorText;

    return (
        <div className="flex flex-col gap-1">
            {error && (
                <Banner
                    text={bannerText}
                    variant={isRetrying ? "progress" : "danger"}
                    className="mx-4 mb-0 mt-4 sm:mx-6"
                />
            )}
            {!error && loadingMessage && (
                <Text className="w-full text-center text-sm text-content-secondary">{loadingMessage}</Text>
            )}
        </div>
    );
};

export const ConversationContent: FC<{
    messages: Message[];
    agentExecution: PlainAgentExecution | null;
    onClick?: (content: AgentMessageFragment) => void;
    className?: string;
    environmentId?: string;
    onSendMessage?: (message: string) => void;
    pendingInteractiveBlock?: PendingInteractiveFragment | null;
}> = ({ messages, agentExecution, onClick, className, environmentId, onSendMessage, pendingInteractiveBlock }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const scrollAreaRef = useRef<ScrollAreaHandle>(null);
    const chatMessagesRef = useRef<ChatMessagesHandle>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [, setInputValue] = useAtom(agentExecutionInputAtomFamily(environmentId ?? ""));
    const scrollButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const wasAtBottomRef = useRef(true);

    const handleScrollToBottom = useCallback(() => {
        chatMessagesRef.current?.scrollToBottom();
    }, []);

    const handleScrollStateChange = useCallback((isAtBottom: boolean) => {
        const wasAtBottom = wasAtBottomRef.current;
        wasAtBottomRef.current = isAtBottom;

        if (scrollButtonTimeoutRef.current) {
            clearTimeout(scrollButtonTimeoutRef.current);
            scrollButtonTimeoutRef.current = null;
        }

        if (!isAtBottom) {
            // Delay when transitioning away from bottom to avoid flash during auto-scroll
            if (wasAtBottom) {
                scrollButtonTimeoutRef.current = setTimeout(() => {
                    setShowScrollButton(true);
                }, 500);
            } else {
                setShowScrollButton(true);
            }
        } else {
            setShowScrollButton(false);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (scrollButtonTimeoutRef.current) {
                clearTimeout(scrollButtonTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="relative flex h-full min-h-0 flex-1 flex-col">
            <ScrollArea
                ref={scrollAreaRef}
                orientation="vertical"
                autoScroll={true}
                autoScrollContentRef={contentRef}
                scrollRestorationKey={agentExecution?.id}
                id="conversation-content"
                className={cn("mx-[2px] my-[2px] flex-1 pb-8 pl-[var(--scrollbar-width)]", className)}
                onScrollStateChange={handleScrollStateChange}
            >
                {agentExecution ? (
                    <div ref={contentRef}>
                        <ChatMessages
                            ref={chatMessagesRef}
                            agentExecution={agentExecution}
                            messages={messages}
                            onClick={onClick}
                            onSendMessage={onSendMessage}
                            pendingInteractiveBlock={pendingInteractiveBlock}
                            data-tracking-id-none
                        />
                    </div>
                ) : (
                    <div className="flex h-full min-h-[200px] items-center justify-center">
                        <div className="flex flex-col items-center text-center">
                            <Suspense fallback={<OnaLettermark className="mb-6 size-8 text-content-primary" />}>
                                <OnaWaiting className="mb-6" />
                            </Suspense>
                            <Heading1 className="text-wrap">What do you want to get done today?</Heading1>
                            <p className="text-lg text-content-muted">Here are some suggestions to get you started</p>

                            <div className="mt-8 flex flex-wrap justify-center gap-2">
                                {PREDEFINED_TASKS.map((task) => (
                                    <PromptTemplate
                                        key={task.taskName}
                                        Icon={task.Icon}
                                        iconColor={task.iconColor}
                                        iconBackgroundColor={task.iconBackgroundColor}
                                        title={task.title}
                                        description={task.description}
                                        selected={false}
                                        data-tracking-id={`prompt-template-${task.taskName}-conversation`}
                                        onSelectChange={() => {
                                            setInputValue(task.prompt);
                                        }}
                                        size="sm"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </ScrollArea>
            <ScrollToBottomButton
                onClick={handleScrollToBottom}
                visible={showScrollButton}
                data-tracking-id-none // tracking is handled by the button inside ScrollToBottomButton
            />
        </div>
    );
};

/**
 * Returns true if `el` (or any ancestor up to `boundary`) can still scroll
 * in the direction indicated by `deltaY`.
 */
function canScrollInDirection(el: EventTarget | null, deltaY: number, boundary: HTMLElement): boolean {
    let node = el as HTMLElement | null;
    while (node && node !== boundary) {
        const { overflowY } = getComputedStyle(node);
        const scrollable = overflowY === "auto" || overflowY === "scroll";
        if (scrollable) {
            const atTop = node.scrollTop <= 0;
            const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
            if (deltaY < 0 && !atTop) return true;
            if (deltaY > 0 && !atBottom) return true;
        }
        node = node.parentElement;
    }
    return false;
}

export const ConversationInput: FC<{
    agentExecution: PlainAgentExecution | null;
    hasMessages: boolean;
    onNewMessage: (message: NewPendingMessage) => void;
    messages: Message[];
    environmentId?: string;
    pendingInteractiveBlock?: React.ReactNode;
    pendingInteractiveBlockType?: "clarifyingQuestions" | "nextStepsProposal";
}> = ({
    agentExecution,
    hasMessages,
    onNewMessage,
    messages,
    environmentId,
    pendingInteractiveBlock,
    pendingInteractiveBlockType,
}) => {
    const [activeState, setActiveState] = useState<ChatInputActiveState | null>(null);

    const isOnaWorking =
        agentExecution &&
        agentExecution.status?.phase !== AgentExecution_Phase.WAITING_FOR_INPUT &&
        agentExecution.status?.phase !== AgentExecution_Phase.STOPPED;

    const placeholder = isOnaWorking ? "Add a follow up task or command" : undefined;

    const floodMode = activeState?.mode ?? AgentMode.Agent;
    const floodActive = activeState?.isActive ?? false;

    const hasClarifyingQuestions = pendingInteractiveBlockType === "clarifyingQuestions";

    const inputContainerRef = useRef<HTMLDivElement>(null);

    // Forward wheel events to the conversation scroll area when nothing inside
    // the input area can handle the scroll (e.g. the textarea isn't overflowing).
    const handleWheel = useCallback((e: React.WheelEvent) => {
        if (inputContainerRef.current && canScrollInDirection(e.target, e.deltaY, inputContainerRef.current)) {
            return;
        }
        const scrollContainer = document.getElementById("conversation-content");
        if (scrollContainer) {
            scrollContainer.scrollTop += e.deltaY;
        }
    }, []);

    return (
        <div ref={inputContainerRef} className="mt-0 flex-shrink-0" onWheel={handleWheel}>
            <div className={cn("bg-surface-default flex justify-center")}>
                <div className="relative flex w-full min-w-0 max-w-[700px] flex-shrink-0 flex-col items-center px-4 pb-4 sm:px-6">
                    <div className="absolute bottom-full h-8 w-full max-w-[95%] bg-gradient-to-t from-surface-01 to-transparent" />

                    {agentExecution ? (
                        <div
                            className={cn(
                                "chatinput-flood-wrapper w-full overflow-hidden rounded-xl p-2",
                                floodActive && "flood-active",
                            )}
                            style={{ "--chatinput-flood-bg": getAgentModeFloodBgVar(floodMode) } as React.CSSProperties}
                        >
                            {pendingInteractiveBlock}
                            <div className="relative z-10">
                                <ChatInput
                                    hasMessages={hasMessages}
                                    agentExecution={agentExecution}
                                    environmentId={environmentId}
                                    onNewMessage={onNewMessage}
                                    placeholder={placeholder}
                                    onActiveStateChange={setActiveState}
                                    disableModeSwitching={hasClarifyingQuestions}
                                    autoFocus
                                />
                            </div>
                        </div>
                    ) : environmentId ? (
                        <div
                            className="chatinput-flood-wrapper chatinput-flood-wrapper-css w-full overflow-hidden rounded-xl p-1.5"
                            style={
                                {
                                    "--chatinput-flood-bg": getAgentModeFloodBgVar(AgentMode.Agent),
                                } as React.CSSProperties
                            }
                        >
                            {pendingInteractiveBlock}
                            <ChatInputStyled environmentId={environmentId} />
                        </div>
                    ) : null}

                    {agentExecution && (
                        <div className="mt-1 flex w-full items-center justify-between pr-2">
                            <LastUserMessageButton messages={messages} />
                            <div className="ml-auto flex items-center gap-3">
                                <ModelIndicator agentExecution={agentExecution} />
                                <StopButton agentExecution={agentExecution} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
